import React, { useState, useEffect, useRef } from 'react'

import { PlusOutlined, EditOutlined, DragOutlined, CloseOutlined, SaveOutlined } from '@ant-design/icons'
import { Typography, Row, Col, Button, Modal, List, message as antMessage, Space, Empty } from 'antd'
import { useTranslation } from 'react-i18next'

import { dashboardService } from '@services/admin/dashboardService'
import { workflowService } from '@services/admin/workflowService'
import { DEFAULT_DASHBOARD_CONFIG, WorkflowWidget } from './WidgetConfig'

const { Title } = Typography

const DashboardOverview = () => {
  const { t } = useTranslation()
  const [widgets, setWidgets] = useState([])
  const [availableWorkflows, setAvailableWorkflows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const dragItem = useRef(null)
  const dragOverItem = useRef(null)

  // 載入 Dashboard 配置和 Workflows
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        // 並行載入 dashboard 設定和 workflows
        const [settingsData, workflowsData] = await Promise.all([
          dashboardService.getSettings(),
          workflowService.fetchList()
        ])

        // 設定 workflows
        if (workflowsData.success) {
          setAvailableWorkflows(workflowsData.data || [])
        }

        // 設定 dashboard widgets
        if (settingsData.success && settingsData.data?.widgets) {
          setWidgets(settingsData.data.widgets)
        } else {
          setWidgets(DEFAULT_DASHBOARD_CONFIG)
        }
      } catch (error) {
        console.error('[Dashboard] Load data error:', error)
        setWidgets(DEFAULT_DASHBOARD_CONFIG)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  // 保存 Dashboard 配置
  const saveDashboardConfig = async () => {
    try {
      setSaving(true)
      const result = await dashboardService.saveSettings(widgets)

      if (result.success) {
        antMessage.success(t('admin.dashboard.save_success'))
        setEditMode(false)
      } else {
        antMessage.error(result.message || t('admin.dashboard.save_error'))
      }
    } catch (error) {
      console.error('[Dashboard] Save config error:', error)
      antMessage.error(t('admin.dashboard.save_error'))
    } finally {
      setSaving(false)
    }
  }

  // 添加 Widget
  const addWidget = (workflow) => {
    const widgetId = `workflow_${workflow.id}`
    const exists = widgets.find(w => w.id === widgetId)
    if (exists) {
      antMessage.warning(t('admin.dashboard.widget_already_added'))
      return
    }

    const newWidget = {
      id: widgetId,
      type: 'workflow',
      workflow_id: workflow.id,
      name: workflow.name,
      position: widgets.length,
    }
    setWidgets([...widgets, newWidget])
    setAddModalVisible(false)
    antMessage.success(t('admin.dashboard.widget_added'))
  }

  // 移除 Widget
  const removeWidget = (widgetId) => {
    const updatedWidgets = widgets
      .filter(w => w.id !== widgetId)
      .map((w, index) => ({ ...w, position: index }))
    setWidgets(updatedWidgets)
    antMessage.success(t('admin.dashboard.widget_removed'))
  }

  // 拖曳開始
  const handleDragStart = (index) => {
    dragItem.current = index
  }

  // 拖曳進入
  const handleDragEnter = (index) => {
    dragOverItem.current = index
  }

  // 拖曳結束
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    if (dragItem.current === dragOverItem.current) return

    const newWidgets = [...widgets]
    const draggedItem = newWidgets[dragItem.current]

    newWidgets.splice(dragItem.current, 1)
    newWidgets.splice(dragOverItem.current, 0, draggedItem)

    const updatedWidgets = newWidgets.map((w, index) => ({ ...w, position: index }))
    setWidgets(updatedWidgets)

    dragItem.current = null
    dragOverItem.current = null
  }

  // 可添加的 Workflows（排除已添加的）
  const availableToAdd = availableWorkflows.filter(
    wf => !widgets.find(w => w.workflow_id === wf.id)
  )

  // 渲染 Widget
  const renderWidget = (widgetConfig, index) => {
    return (
      <Col
        key={widgetConfig.id}
        xs={24}
        sm={24}
        md={12}
        lg={12}
        xl={8}
        draggable={editMode}
        onDragStart={() => handleDragStart(index)}
        onDragEnter={() => handleDragEnter(index)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => e.preventDefault()}
        style={{
          cursor: editMode ? 'move' : 'default',
          opacity: dragItem.current === index ? 0.5 : 1,
        }}
      >
        <div style={{ position: 'relative' }}>
          {editMode && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <DragOutlined />
                {t('admin.dashboard.drag_to_reorder')}
              </div>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => removeWidget(widgetConfig.id)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              />
            </>
          )}
          <WorkflowWidget workflow={widgetConfig} />
        </div>
      </Col>
    )
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <Title level={2} className="admin-page-title">
          {t('admin.dashboard.title')}
        </Title>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          {t('common.loading')}
        </div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} className="admin-page-title" style={{ margin: 0 }}>
          {t('admin.dashboard.title')}
        </Title>
        <Space>
          {!editMode ? (
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditMode(true)}
            >
              {t('admin.dashboard.edit_mode')}
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveDashboardConfig}
                loading={saving}
              >
                {t('common.button.save')}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={() => setEditMode(false)}
              >
                {t('common.button.cancel')}
              </Button>
            </>
          )}
        </Space>
      </div>

      {editMode && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
            block
            disabled={availableToAdd.length === 0}
          >
            {t('admin.dashboard.add_widget')}
          </Button>
        </div>
      )}

      {widgets.length === 0 ? (
        <Empty
          description={t('admin.dashboard.no_widgets')}
          style={{ padding: '40px 0' }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(true)
              setAddModalVisible(true)
            }}
          >
            {t('admin.dashboard.add_first_widget')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {widgets
            .sort((a, b) => a.position - b.position)
            .map((widget, index) => renderWidget(widget, index))}
        </Row>
      )}

      {/* 添加 Widget Modal */}
      <Modal
        title={t('admin.dashboard.add_widget')}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        width={600}
      >
        {availableToAdd.length === 0 ? (
          <Empty description={t('admin.dashboard.no_workflows_available')} />
        ) : (
          <List
            dataSource={availableToAdd}
            renderItem={(workflow) => (
              <List.Item
                actions={[
                  <Button
                    key="add"
                    type="primary"
                    size="small"
                    onClick={() => addWidget(workflow)}
                  >
                    {t('common.button.add')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={workflow.name}
                  description={workflow.description}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  )
}

export default DashboardOverview
